import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Container,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.css';
import { API_URL } from '../config/api';

const SurveyPage = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [survey, setSurvey] = useState(null);
  const [surveyModel, setSurveyModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleSurveyComplete = useCallback(async (sender) => {
    try {
      const responses = [];
      Object.keys(sender.data).forEach(key => {
        responses.push({
          questionName: key,
          answer: sender.data[key]
        });
      });

      await axios.post(`${API_URL}/surveys/surveys/${surveyId}/respond`, {
        surveyId: surveyId,
        responses
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Survey completed successfully!');
      navigate('/existing?tab=feedback'); // Go back to existing employee page with feedback tab
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Failed to submit survey');
    }
  }, [surveyId, getAuthHeaders, navigate]);

  useEffect(() => {
    if (surveyId) {
      fetchSurvey();
    }
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/surveys/surveys/${surveyId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.hasResponded) {
        toast.info('You have already completed this survey');
        navigate(-1);
        return;
      }

      setSurvey(response.data.survey);
      
      // Group questions by section
      const groupedQuestions = (response.data.survey.questions || []).reduce((acc, question) => {
        const section = question.section || 'General';
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(question);
        return acc;
      }, {});

      // Convert questions to SurveyJS format with sections as panels
      const surveyElements = [];
      
      Object.entries(groupedQuestions).forEach(([sectionName, sectionQuestions], sectionIndex) => {
        // Add section header as a panel
        surveyElements.push({
          type: 'panel',
          name: `section_${sectionIndex}`,
          title: sectionName,
          showTitle: true,
          showNumber: false,
          startWithNewLine: true,
          elements: sectionQuestions.map((question, index) => {
            // Map question types to SurveyJS types
            let surveyType = question.type;
            if (question.type === 'comment') surveyType = 'comment';
            if (question.type === 'radio') surveyType = 'radiogroup';
            if (question.type === 'checkbox') surveyType = 'checkbox';

            if (question.type === 'rating') surveyType = 'radiogroup'; // Convert rating to radiogroup for custom scales
            if (question.type === 'boolean') surveyType = 'radiogroup'; // Convert boolean to radiogroup for radio button style
            if (question.type === 'text') surveyType = 'text';

            // Handle choices for different question types
            let choices = question.choices || [];
            
            // For boolean questions, create radio button choices
            if (question.type === 'boolean') {
              choices = [
                { value: true, text: 'Yes' },
                { value: false, text: 'No' }
              ];
            }
            
            // For rating questions, use the predefined choices based on rating scale
            if (question.type === 'rating' && question.choices && question.choices.length > 0) {
              choices = question.choices;
            }

            return {
              type: surveyType,
              name: question.name,
              title: question.title,
              isRequired: question.isRequired,
              choices: choices,
              minRateDescription: question.minRateDescription,
              maxRateDescription: question.maxRateDescription,
              min: question.min,
              max: question.max,
              step: question.step
            };
          })
        });
      });

      // Create survey model
      const surveyJson = {
        description: response.data.survey.description,
          elements: surveyElements
      };

      if (!response.data.survey.questions || response.data.survey.questions.length === 0) {
        setError('This survey has no questions');
        return;
      }

      const model = new Model(surveyJson);
      
      // Ensure the model is properly configured
      model.showQuestionNumbers = true;
      model.showProgressBar = false;
      model.questionsOnPageMode = "singlePage";
      
      // Add completion handler
      model.onComplete.add(handleSurveyComplete);

      setSurveyModel(model);
    } catch (error) {
      console.error('Error loading survey:', error);
      setError('Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/existing?tab=feedback');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: 2 }}>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mb: 2 }}
        >
          Back to Surveys
        </Button>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" fontWeight="bold">
                {survey?.title}
              </Typography>
            </Box>
            
            {survey?.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {survey.description}
              </Typography>
            )}
            
            <Typography variant="body2" color="text.secondary">
              Questions: {survey?.questions?.length || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Survey Content */}
      <Paper elevation={2} sx={{ p: 3 }}>
        {surveyModel && (
             <Box
       sx={{
         width: '100%',
         '& .sd-container-modern': {
           width: '100% !important',
           maxWidth: '100% !important',
           margin: '0 !important',
           padding: '0 !important',
         },
            '& .sd-root-modern': {
           padding: '0 !important',
           width: '100% !important',
           maxWidth: '100% !important',
         },
         '& .sd-body': {
           background: 'transparent',
           width: '100% !important',
           maxWidth: '100% !important',
         },
              '& .sd-page': {
           width: '100% !important',
           maxWidth: '100% !important',
         },
         '& .sd-panel': {
           width: '100% !important',
           maxWidth: '100% !important',
              },
              '& .sd-question': {
           width: '100% !important',
           maxWidth: '100% !important',
              },
              '& .sd-input': {
           width: '100% !important',
           maxWidth: '100% !important',
              },
              '& .sd-text': {
           width: '100% !important',
           maxWidth: '100% !important',
         },
         '& .sd-comment': {
           width: '100% !important',
           maxWidth: '100% !important',
         },
         '& .sd-question__title': {
           fontSize: '3rem !important',
           fontWeight: '500 !important',
           letterSpacing: '0.5px !important',
         },
         '& .sd-panel__title': {
           fontSize: '1.8rem !important',
           fontWeight: '700 !important',
         },
         '& .sd-input': {
           fontSize: '1.3rem !important',
         },
         '& .sd-text': {
           fontSize: '1.3rem !important',
         },
         '& .sd-comment': {
           fontSize: '1.3rem !important',
         },
         '& .sd-radiogroup__item-text': {
           fontSize: '1.3rem !important',
         },
         '& .sd-checkbox__item-text': {
           fontSize: '1.3rem !important',
         },
         '& .sd-rating__item': {
           marginRight: '8px !important',
           marginLeft: '8px !important',
         },
         '& .sd-rating__item:first-child': {
           marginLeft: '0 !important',
         },
         '& .sd-rating__item:last-child': {
           marginRight: '0 !important',
         }
       }}
     >
            <Survey model={surveyModel} />
          </Box>
    
      
        )}
      </Paper>
    </Container>
  );
};

export default SurveyPage; 